import { Dispatch, SetStateAction } from 'react';

import { Spacing } from '@kezios/design-system';
import {
  Center,
  ContainerProps,
  Flex,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';

import { SectionHeader } from '../../SectionHeader/SectionHeader';
import { SectionLayout } from '../../SectionLayout/SectionLayout';
import { Arrow } from './Arrow/Arrow';
import { Item, ItemProps } from './Item/Item';

import css from './PricingSectionLayout.module.scss';

export type PricingSectionProps<T extends string> = {
  children:
    | React.ReactElement<ItemProps<T>>[]
    | React.ReactElement<ItemProps<T>>;
  category?: string;
  title?: string;
  subtitle?: string;
  noHeader?: boolean;
  cardCategories: { value: T; label: string }[];
  onChange: Dispatch<SetStateAction<string>>;
  segmentControlDecorationText?: string;
} & Omit<ContainerProps, 'children' | 'onChange'>;

export const PricingSectionLayout = <T extends string>({
  category,
  title,
  subtitle,
  noHeader = false,
  children,
  cardCategories,
  onChange,
  segmentControlDecorationText = undefined,
  ...props
}: PricingSectionProps<T>) => {
  return (
    <Stack gap={Spacing.s16}>
      {!noHeader && (
        <SectionLayout {...props}>
          <SectionHeader
            variant="left"
            category={category as string}
            title={title as string}
            subtitle={subtitle as string}
          />
        </SectionLayout>
      )}
      <Stack gap={Spacing.s8}>
        <Center w={'100%'} h={'fit-content'}>
          <div className={css.segmentedControlContainer}>
            {segmentControlDecorationText && (
              <div className={`${css.segmentedControlDecoration} desktop`}>
                <Text variant="textSm.semibold" c={'primary.7'}>
                  {segmentControlDecorationText}
                </Text>
                <Arrow color="primary.7" />
              </div>
            )}
            <SegmentedControl
              data={cardCategories}
              onChange={(value: T) => onChange(value)}
            />
          </div>
        </Center>
        <Flex gap={Spacing.s8} justify={'center'} wrap={'wrap'}>
          {children}
        </Flex>
      </Stack>
    </Stack>
  );
};

PricingSectionLayout.Card = Item;
